import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Button,
  Skeleton,
  useDisclosure,
  Link,
} from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@wc/domain/authn";
import { formatDate } from "@wc/domain/dates";
import { listUserOrganizations } from "@wc/domain/organizations";
import CreateOrgModal from "@wc/domain/organizations/components/create-org-modal";

/**
 * Organization list component showing all orgs for the current user
 * @returns {React.ReactElement} The org list
 */
export default function OrgList() {
  const { t } = useTranslation();

  const authData = useAuth();
  const [orgs, setOrgs] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const fetchOrgs = React.useCallback(async () => {
    if (!authData.isLoggedIn()) return;

    try {
      setIsLoading(true);
      const userOrgs = await listUserOrganizations(
        authData.getCurrentUser().id,
      );
      setOrgs(userOrgs);
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
    } finally {
      setIsLoading(false);
    }
  }, [authData]);

  React.useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  const handleOrgCreated = () => {
    fetchOrgs();
  };

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("org.listTitle")}</h1>
        <Button color="primary" onPress={onOpen}>
          {t("org.createOrg")}
        </Button>
      </div>

      <Table aria-label="Organizations table">
        <TableHeader>
          <TableColumn>Name</TableColumn>
          <TableColumn>{t("org.yourRole")}</TableColumn>
          <TableColumn>{t("org.joinedAt")}</TableColumn>
        </TableHeader>
        <TableBody emptyContent={t("org.noOrgs")}>
          {isLoading
            ? Array(3)
                .fill()
                .map((_, index) => (
                  <TableRow key={`loading-row-${index}`}>
                    <TableCell>
                      <Skeleton className="h-4 w-3/4 rounded" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-1/2 rounded" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-1/2 rounded" />
                    </TableCell>
                  </TableRow>
                ))
            : orgs.map((item) => (
                <TableRow key={item.org.id}>
                  <TableCell>
                    <Link href={`/orgs/${item.org.id}/overview`}>
                      {item.org.displayName}
                    </Link>
                  </TableCell>
                  <TableCell>{item.role}</TableCell>
                  <TableCell>{formatDate(item.joinedAt)}</TableCell>
                </TableRow>
              ))}
        </TableBody>
      </Table>

      <CreateOrgModal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        onSuccess={handleOrgCreated}
      />
    </div>
  );
}
